"use client";

import React, { useState, useEffect, useMemo } from 'react';
import { HeroSection } from '@/components/shared/HeroSection';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, School, GraduationCap, LayoutGrid, List, AlertCircle } from 'lucide-react';
import { InstitutesTab } from '@/components/institutes-and-programs/InstitutesTab';
import { ProgramsTab } from '@/components/institutes-and-programs/ProgramsTab';
import { cn } from '@/lib/utils';
import { Institute, Program } from '@/lib/types';
import { fetchJsonData, validateCCMTData } from '@/lib/data-handlers';
import { Button } from '@/components/ui/button';



export default function InstitutesAndProgramsPage() {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'institutes' | 'programs'>('institutes');
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        const result = await fetchJsonData('/ccmt_2025.json');
        if (result.error) {
          setError(result.error);
          setData([]);
        } else if (result.data) {
          setData(result.data);
          setError(null);
        } else {
          setError('No data received');
          setData([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
        setData([]);
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  const { institutes, programs } = useMemo(() => {
    if (data.length === 0) return { institutes: [], programs: [] };

    const instituteMap = new Map<string, Set<string>>();
    const programMap = new Map<string, Set<string>>();

    data.forEach(item => {
      // Process Institutes
      if (!instituteMap.has(item.institute)) {
        instituteMap.set(item.institute, new Set());
      }
      instituteMap.get(item.institute)?.add(item.program);

      // Process Programs
      if (!programMap.has(item.program)) {
        programMap.set(item.program, new Set());
      }
      programMap.get(item.program)?.add(item.institute);
    });

    const categorizedInstitutes: Institute[] = Array.from(instituteMap.entries()).map(([name, programSet]) => {
      let category: Institute['category'] = "Other";
      const upperName = name.toUpperCase();

      if (upperName.includes("NATIONAL INSTITUTE OF TECHNOLOGY") || upperName.startsWith("NIT ")) {
        category = "NIT";
      } else if (upperName.includes("INDIAN INSTITUTE OF INFORMATION TECHNOLOGY") || upperName.includes("(IIIT)")) {
        category = "IIIT";
      } else if (upperName.includes("UNIVERSITY") || upperName.includes("VISHWAVIDYALAYA")) {
        category = "State University";
      }

      return {
        name,
        category,
        programs: Array.from(programSet).sort((a, b) => a.localeCompare(b))
      };
    }).sort((a, b) => a.name.localeCompare(b.name));

    const programList: Program[] = Array.from(programMap.entries()).map(([name, instituteSet]) => ({
      name,
      institutes: Array.from(instituteSet).sort((a, b) => a.localeCompare(b))
    })).sort((a, b) => a.name.localeCompare(b.name));

    return { institutes: categorizedInstitutes, programs: programList };
  }, [data]);

  if (isLoading) return <LoadingScreen />;

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="bg-destructive/10 border border-destructive/20 rounded-3xl p-8 text-center space-y-4">
            <div className="flex justify-center">
              <div className="size-16 rounded-full bg-destructive/10 border border-destructive/20 flex items-center justify-center">
                <AlertCircle className="size-8 text-destructive" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground mb-2">Unable to Load Institutions</h2>
              <p className="text-sm text-on-surface-variant leading-relaxed">{error}</p>
            </div>
            <div className="flex gap-3 justify-center pt-4">
              <Button
                onClick={() => window.location.reload()}
                className="bg-primary text-primary-foreground"
              >
                Retry
              </Button>
              <Button
                onClick={() => window.history.back()}
                variant="outline"
              >
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="container mx-auto px-4 py-8 sm:px-6 lg:py-16">
        <HeroSection
          title={<>CCMT Participating <span className="text-primary">Colleges</span> & M.Tech Programs</>}
          description={<>Discover and explore all participating <span className="text-on-surface font-bold">NITs, IIITs, and GFTIs</span>. Browse by institute category or search by specific academic programs.</>}
          hideInfoBox={true}
        />

        {/* Tab Navigation & Search */}
        <div className="mt-8 flex flex-col md:flex-row gap-6 items-center justify-between">
          <div className="flex p-1 bg-surface-container rounded-2xl border border-outline-variant/20 w-full md:w-auto">
            <button
              onClick={() => setActiveTab('institutes')}
              className={cn(
                "flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300",
                activeTab === 'institutes'
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20 scale-[1.02]"
                  : "text-on-surface-variant hover:text-primary hover:bg-primary/5"
              )}
            >
              <School className="size-4" />
              Institutions
            </button>
            <button
              onClick={() => setActiveTab('programs')}
              className={cn(
                "flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300",
                activeTab === 'programs'
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20 scale-[1.02]"
                  : "text-on-surface-variant hover:text-primary hover:bg-primary/5"
              )}
            >
              <GraduationCap className="size-4" />
              Programs
            </button>
          </div>

          <div className="relative w-full md:max-w-md group">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 size-5 text-on-surface-variant group-focus-within:text-primary transition-colors pointer-events-none" />
            <input
              type="text"
              placeholder={`Search ${activeTab === 'institutes' ? 'institutions' : 'programs'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-14 pr-6 py-4 bg-surface-container-low border border-outline-variant/30 rounded-3xl text-[16px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm md:py-3.5 md:text-sm"
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="mt-10">
          <AnimatePresence mode="wait">
            {activeTab === 'institutes' ? (
              <motion.div
                key="institutes-tab"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <InstitutesTab institutes={institutes} searchQuery={searchQuery} />
              </motion.div>
            ) : (
              <motion.div
                key="programs-tab"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ProgramsTab programs={programs} searchQuery={searchQuery} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {/* Static Editorial Content — Crawlable by Google */}
        <section className="mt-20 max-w-4xl mx-auto space-y-10 pb-10">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-display font-black text-foreground mb-3">About CCMT Participating Institutions & Programs</h2>
            <p className="text-on-surface-variant text-sm md:text-base max-w-2xl mx-auto">A comprehensive guide to understanding the types of institutes, M.Tech program categories, and how to evaluate colleges for your CCMT choice list.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">National Institutes of Technology (NITs)</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                NITs are premier, centrally funded technical universities established under the NIT Act. There are 31 NITs across India, including NIT Trichy, NIT Warangal, NIT Surathkal, NIT Rourkela, and VNIT Nagpur. They are consistently ranked among the top engineering colleges in India by the NIRF. NIT seats are highly competitive, and cutoffs for General category candidates at top NITs in popular branches like Computer Science and Electronics can reach 800+ GATE score.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">IIITs & Government Funded Technical Institutes (GFTIs)</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                IIITs (Indian Institutes of Information Technology) are specialized institutions focused on IT and allied disciplines. GFTIs are government-funded institutes that also participate in CCMT counseling. IIEST Shibpur (formerly BE College), one of the oldest engineering institutions in Asia, is a notable GFTI. GFTIs and IIITs often have lower cutoffs than top NITs and can be excellent choices for candidates with mid-range GATE scores.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">Understanding NIRF Rankings</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                The National Institutional Ranking Framework (NIRF) ranks universities across Teaching & Learning, Research, Graduation Outcomes, Outreach, and Perception. When comparing institutes with similar cutoffs, always check NIRF rank as a tiebreaker. An institute ranked in the top 25 in the Engineering category by NIRF is likely to offer better placement opportunities, research facilities, and faculty quality. Our platform integrates NIRF rankings directly alongside CCMT data.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">Popular M.Tech Specializations in CCMT</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                The most popular M.Tech programs through CCMT include <strong className="text-foreground">Computer Science & Engineering, Data Science & Artificial Intelligence, Electronics & Communication Engineering, VLSI Design, Power Systems, Structural Engineering, Machine Design, Chemical Engineering, and Signal Processing</strong>. CS and DA programs have the highest competition, often with closing ranks exceeding 600-700 for top NITs. Specialized programs in niche areas like Nanotechnology or Materials Science typically have lower cutoffs.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">How to Evaluate an Institute for M.Tech</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                Beyond cutoffs and NIRF rank, consider: (1) <strong className="text-foreground">Placement statistics</strong> — average and highest M.Tech placement packages differ widely between institutes. (2) <strong className="text-foreground">Research output</strong> — if you plan to pursue a PhD, look for institutes with active research groups in your area. (3) <strong className="text-foreground">Location</strong> — metropolitan NITs like NIT Surathkal (Mangalore) or VNIT Nagpur offer better industry exposure. (4) <strong className="text-foreground">Specialization alignment</strong> — ensure the specific M.Tech program offered matches your career goals.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
